import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { EnvProvider } from './EnvContext';
import Layout from './Layout';
import LiveView from './LiveView';
import Layout2 from './live2/Layout2';
import LiveView2 from './live2/LiveView2';
import HomePage from './HomePage';
import AnalysisLanding from './AnalysisLanding';
import MessagesPage from './analysis/MessagesPage';
import S2LiveMap from './analysis/S2LiveMap';
import NetworkMap from './analysis/NetworkMap';
import LoginPage from './LoginPage';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#0d1b2a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#f0a500' }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <EnvProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Navigate to="/home" replace />
              </ProtectedRoute>
            } />
            <Route path="/live" element={
              <ProtectedRoute>
                <Layout>
                  <LiveView />
                </Layout>
              </ProtectedRoute>
            } />
            {/* New Firestore process: live_journeys + live_journey_occupancy.
                Parallel to /live rather than replacing it — the two document
                shapes have nothing in common, so a half-migrated board is not a
                runnable state, and rollback is a URL. */}
            <Route path="/live2" element={
              <ProtectedRoute>
                <Layout2>
                  <LiveView2 />
                </Layout2>
              </ProtectedRoute>
            } />
            {/* Landing page concept — layout/style review, placeholder figures.
                Parallel to /live rather than replacing it; / still redirects to
                /live until the design is signed off. */}
            <Route path="/home" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="/analysis" element={
              <ProtectedRoute>
                <AnalysisLanding />
              </ProtectedRoute>
            } />
            <Route path="/analysis/messages" element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            } />
            {/* Whole-network map, driven by live_journeys. The original
                single-line S2 map stays reachable at /analysis/coverage-s2. */}
            <Route path="/analysis/coverage" element={
              <ProtectedRoute>
                <NetworkMap />
              </ProtectedRoute>
            } />
            <Route path="/analysis/coverage-s2" element={
              <ProtectedRoute>
                <S2LiveMap />
              </ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </EnvProvider>
    </BrowserRouter>
  );
};

export default App;
